/**
 * AdminShopPicker — searchable shop picker shared across every admin
 * tab that scopes work to a single shop (Categories, Products,
 * Inventory, Coupons, Settlements, Booking Settlements, Orders,
 * Bookings, Hours, Pricing, …).
 *
 * Originally shipped on AdminPricing in iter62b. iter84 extracts it so
 * every shop-scoped admin tab can offer the same searchable UX without
 * 9 copy-pasted dropdowns. Searches by:
 *   • shop name
 *   • shop primary mobile_number + whatsapp_number
 *   • each linked owner's mobile_number + first/last name
 *
 * Shops are loaded once per mount from ``/admin/restaurants``, sorted
 * alphabetically by name. Owners are loaded once from ``/admin/owners``
 * so the search input can match by owner phone too.
 *
 * Props (every prop is optional):
 *   value             selected shop id; "" or null = "All shops" / unselected
 *   onChange(id)      fires with the picked id ("" if All-shops picked)
 *   allShopsOption    bool, default false. Prepend an "All shops" row
 *                     whose pick returns "".
 *   onlyActive        bool, default true. Hide soft-deleted shops.
 *   placeholder       button label when nothing is selected
 *   testIdPrefix      string used to build data-testids per page
 *   className         extra classes for the trigger button
 *   shopsOverride     pre-loaded shops (bypasses internal fetch)
 *   ownersOverride    pre-loaded owners
 *   onShopsLoaded     callback fired once shops are fetched (lets a
 *                     parent reuse the same array without re-fetching)
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, Search, Store } from "lucide-react";
import { api } from "../lib/api";

export default function AdminShopPicker({
    value,
    onChange,
    allShopsOption = false,
    onlyActive = true,
    placeholder = "Pick a shop",
    testIdPrefix = "shop-picker",
    className = "",
    shopsOverride,
    ownersOverride,
    onShopsLoaded,
}) {
    const [shops, setShops] = useState(shopsOverride || []);
    const [owners, setOwners] = useState(ownersOverride || []);
    const [open, setOpen] = useState(false);
    const [q, setQ] = useState("");
    const wrapRef = useRef(null);
    const searchRef = useRef(null);

    // Load shops + owners on mount unless the parent passed them in.
    useEffect(() => {
        if (shopsOverride) {
            setShops(shopsOverride);
            return;
        }
        api.get("/admin/restaurants")
            .then(({ data }) => {
                let list = data || [];
                if (onlyActive) {
                    list = list.filter((s) => s.status !== "DELETED");
                }
                // Alphabetical so the picker scrolls in a useful order
                // (the API returns rows in creation order which is
                // useless once a tenant has 20+ shops).
                list = [...list].sort((a, b) =>
                    (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" }),
                );
                setShops(list);
                onShopsLoaded?.(list);
            })
            .catch(() => setShops([]));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shopsOverride, onlyActive]);

    useEffect(() => {
        if (ownersOverride) {
            setOwners(ownersOverride);
            return;
        }
        api.get("/admin/owners")
            .then(({ data }) => setOwners(data || []))
            .catch(() => setOwners([]));
    }, [ownersOverride]);

    // Close on outside click.
    useEffect(() => {
        if (!open) return;
        const handler = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) {
                setOpen(false);
                setQ("");
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [open]);

    // Auto-focus the search input when the dropdown opens.
    useEffect(() => {
        if (open && searchRef.current) searchRef.current.focus();
    }, [open]);

    // Build per-shop owner index so the search input can match by owner
    // mobile_number / name too.
    const ownersByShopId = useMemo(() => {
        const m = {};
        for (const o of owners) {
            for (const sid of o.restaurant_ids || []) {
                (m[sid] = m[sid] || []).push(o);
            }
        }
        return m;
    }, [owners]);

    const filtered = useMemo(() => {
        const needle = q.trim().toLowerCase();
        if (!needle) return shops;
        return shops.filter((s) => {
            const ownerMobiles = (ownersByShopId[s.id] || [])
                .map((o) => `${o.mobile_number || ""} ${o.first_name || ""} ${o.last_name || ""}`)
                .join(" ");
            const hay = `${s.name || ""} ${s.mobile_number || ""} ${s.whatsapp_number || ""} ${ownerMobiles}`.toLowerCase();
            return hay.includes(needle);
        });
    }, [shops, ownersByShopId, q]);

    const current = shops.find((s) => s.id === value);
    const label = current?.name
        || (allShopsOption && (value === "" || value == null) ? "All shops" : null)
        || placeholder;

    return (
        <div ref={wrapRef} className={`relative ${className}`}>
            <button
                type="button"
                data-testid={`${testIdPrefix}-trigger`}
                onClick={() => setOpen((v) => !v)}
                className="bg-card border border-border rounded-xl px-3 py-2 text-sm font-semibold inline-flex items-center gap-2 min-w-[220px] justify-between"
            >
                <span className="inline-flex items-center gap-2 truncate max-w-[220px] text-left">
                    <Store size={14} className="text-muted-foreground flex-shrink-0" />
                    <span className="truncate">{label}</span>
                </span>
                <ChevronDown size={14} className={`transition flex-shrink-0 ${open ? "rotate-180" : ""}`} />
            </button>
            {open && (
                // Rendered as a fixed-position sheet (mobile: bottom sheet,
                // sm+: centered dialog) rather than an absolutely-positioned
                // popover anchored to the trigger button. The trigger can
                // sit anywhere in a complex header layout (e.g. next to
                // horizontally-scrolling status-filter pills on
                // AdminBookings) — an anchored popover overflowed off the
                // right edge of the viewport in that case. A fixed sheet
                // is decoupled from the trigger's on-screen position so it
                // always renders fully within the viewport.
                <div
                    className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center"
                    onClick={() => { setOpen(false); setQ(""); }}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        className="bg-card w-full sm:w-[360px] sm:max-w-[calc(100vw-2rem)] rounded-t-2xl sm:rounded-2xl border border-border shadow-xl overflow-hidden flex flex-col max-h-[80vh] sm:max-h-[28rem]"
                    >
                        <div className="px-3 py-3 border-b border-border shrink-0">
                            <div className="flex items-center gap-2 bg-muted rounded-lg px-3 py-2">
                                <Search size={14} className="text-muted-foreground flex-shrink-0" />
                                <input
                                    ref={searchRef}
                                    data-testid={`${testIdPrefix}-search`}
                                    value={q}
                                    onChange={(e) => setQ(e.target.value)}
                                    placeholder="Search by name, phone or owner"
                                    className="bg-transparent outline-none text-sm flex-1 min-w-0"
                                />
                            </div>
                        </div>
                        <div className="flex-1 overflow-y-auto" data-testid={`${testIdPrefix}-list`}>
                            {allShopsOption && !q && (
                                <button
                                    type="button"
                                    data-testid={`${testIdPrefix}-option-all`}
                                    onClick={() => { onChange(""); setOpen(false); setQ(""); }}
                                    className={`w-full text-left px-3 py-2 text-sm hover:bg-muted transition font-semibold border-b border-border ${(!value) ? "bg-primary/10" : ""}`}
                                >
                                    All shops
                                </button>
                            )}
                            {filtered.length === 0 && (
                                <div className="px-3 py-6 text-sm text-muted-foreground text-center">
                                    No shops match &quot;{q}&quot;
                                </div>
                            )}
                            {filtered.map((s) => {
                                const ownerLine = (ownersByShopId[s.id] || [])
                                    .map((o) => o.mobile_number).filter(Boolean).join(" · ");
                                const isActive = s.id === value;
                                return (
                                    <button
                                        key={s.id}
                                        type="button"
                                        data-testid={`${testIdPrefix}-option-${s.id}`}
                                        onClick={() => { onChange(s.id); setOpen(false); setQ(""); }}
                                        className={`w-full text-left px-3 py-2 text-sm hover:bg-muted transition flex flex-col gap-0.5 ${isActive ? "bg-primary/10 font-semibold" : ""}`}
                                    >
                                        <span className="truncate">{s.name}</span>
                                        {(s.mobile_number || ownerLine) && (
                                            <span className="text-[11px] text-muted-foreground truncate">
                                                {s.mobile_number ? `📞 ${s.mobile_number}` : null}
                                                {s.mobile_number && ownerLine ? " · " : ""}
                                                {ownerLine ? `👤 ${ownerLine}` : null}
                                            </span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
